import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { FileText, ChevronLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Terms & Conditions — content sourced verbatim from Sathi Limited's official
 * document (NZBN 9429052213656). Do not edit copy without a legal review.
 */
export default function TermsAndConditions() {
  const onPrint = () => window.print();
  return (
    <div className="min-h-screen bg-[#0A1A33] text-white print:bg-white print:text-black" data-testid="terms-page">
      <div className="print:hidden">
        <Header />
      </div>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20 print:pt-8">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Link to="/" className="inline-flex items-center gap-1 text-caption text-slate-400 hover:text-red-300" data-testid="terms-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Back home
          </Link>
          <Button onClick={onPrint} size="sm" className="btn-gold rounded-full" data-testid="terms-print">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-400/30 grid place-items-center">
            <FileText className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <div className="text-caption">Legal</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight">Terms & Conditions</h1>
          </div>
        </div>
        <p className="text-slate-400 mt-2 text-sm">Triptap · Operated by Sathi Limited · NZBN 9429052213656.</p>

        <article className="mt-10 glass-card gold-border rounded-2xl p-6 sm:p-10 space-y-8 fade-up">
          <section>
            <h2 className="font-display font-bold text-xl text-white mb-3">Introduction</h2>
            <p className="text-slate-300 leading-relaxed text-[15px]">
              These terms and conditions (hereinafter "Terms" or "User Agreement") along with all policies posted on our website Triptap.co.nz (hereinafter referred to as "Websites" or the "Site") set out the terms on which we offer users access to and use of our sites, services, applications, and tools. By accessing, browsing, and using the Site, including availing services mentioned therein, you agree to these terms. If you continue using services from the Site after any change in the User Agreement, this will be considered acknowledgement and agreement of the changed or modified User Agreement. Periodically visit this User Agreement to review the most current terms and conditions. If you do not agree to these terms and conditions, please do not use this Website.
            </p>
          </section>

          <Section n="1" title="About Us">
            <Sub label="Notifications and Communications">
              In connection with your use of our services, we may send you notifications, administrative messages, and other information. You may opt out of some of those communications. Some of our services are also available on mobile devices.
            </Sub>
            <Sub label="Definition of Service">
              Triptap is a modern customer-centric ride-sharing platform designed to provide safe, reliable, and efficient transportation solutions.
            </Sub>
            <Sub label="Secrecy">
              Users agree not to forward or share their passwords with any third party without formal consent from the Company. Doing so will be considered a breach of this agreement, and the Company holds the right to take appropriate legal action.
            </Sub>
            <Sub label="Account Confidentiality">
              Users are liable for maintaining the confidentiality of any login information associated with their account and are responsible for all activities that occur under their account(s).
            </Sub>
          </Section>

          <Section n="2" title="Access">
            <Sub label="Right to Access">
              The Site grants you a non-transferable right to access its services. You are responsible for providing all necessary hardware, software, telephone or other communications equipment, and Internet access to use the Site. The Site or the Company shall not be responsible for any such things.
            </Sub>
            <Sub label="Service Charges">
              Whether services on our Site are free of cost or chargeable will be displayed on the Site itself.
            </Sub>
          </Section>

          <Section n="3" title="Prohibited Activities">
            <p>In connection with using or accessing the services, you will not:</p>
            <Bullets items={[
              ["Legal Compliance", "Breach or circumvent any laws, third-party rights, or our systems, policies, or determinations of your account status."],
              ["Eligibility", "Use our services only if you are able to form legally binding contracts (generally if you are not under 18) or are not temporarily or indefinitely suspended from using our Site."],
              ["Account Transfer", "Transfer your Triptap account and user ID to another party without our consent."],
              ["Malicious Activities", "Distribute viruses or other technologies that may harm us or the interests or property of users."],
              ["Intellectual Property Infringement", "Reproduce, perform, display, distribute, or prepare derivative works from content belonging to or licensed to us or third parties, except with prior express permission."],
              ["Commercial Use", "Commercialize any of our applications or any information or software associated with such applications."],
              ["Data Harvesting", "Harvest or collect information about users without their consent."],
              ["Circumvent Measures", "Circumvent any technical measures we use to provide the services."],
            ]} />
          </Section>

          <Section n="4" title="Abuse of Triptap Account">
            <Sub label="Sanctions">
              If we believe you are abusing Triptap, we may, in our sole discretion, limit, suspend, or terminate your user account(s), access to our services, delay or remove hosted content, remove any special status associated with your account(s), reduce or eliminate any discounts, and take technical and/or legal steps to prevent you from using our services.
            </Sub>
            <Sub label="Account Inactivity">We may cancel unconfirmed accounts or accounts that have been inactive for a long time.</Sub>
            <Sub label="Service Modification">We reserve the right to modify or discontinue our services.</Sub>
            <Sub label="Service Termination">We may refuse or terminate our services to anyone for any reason at our discretion.</Sub>
          </Section>

          <Section n="5" title="Refund Policy">
            <p>Triptap aims to provide satisfaction with its services at reasonable rates.</p>
            <p>Refund requests should be reviewed according to the master service agreement. The decision on refund requests is final and binding, considering factors beyond control like holidays, government delays, natural disasters, etc. Refunds are offered under specific conditions, such as non-use of services for more than a day, and only once per user account. Generally, payments made are non-refundable.</p>
          </Section>

          <Section n="6" title="Authorization to Contact and Recording Calls">
            <p>Users consent to receive calls and text messages for account notifications, troubleshooting, debt collection, surveys, promotions, and other necessary communications. Triptap may share your phone numbers with service providers for authorized purposes only, and may monitor or record calls for quality control or protection purposes.</p>
            <Sub label="Software">
              Any program or software from the Site is owned by Triptap or third-party providers, with all copyrights reserved. Users must agree to the terms of any license agreement before downloading, copying, or installing software.
            </Sub>
            <Sub label="Content">
              Users must use the Site for lawful purposes and adhere to all applicable laws and regulations. Prohibited actions include criminal activity, posting offensive content, altering communications, disrupting the Site, false representations, unsolicited advertising, and intellectual property infringement. Users agree to indemnify Triptap for any breach of the User Agreement.
            </Sub>
            <Sub label="Links to Other Websites">
              Third-party links are provided for convenience and are owned and controlled by third parties. Triptap is not responsible for the content or availability of third-party sites and does not endorse them. Users visit linked sites at their own risk and Triptap is not liable for any damages from their use. Triptap acknowledges the ownership of trademarks, logos, or copyrights by third parties displayed on the Site.
            </Sub>
          </Section>

          <Section n="7" title="Termination">
            <p>If dissatisfied with any service, policy, or content, users must discontinue accessing the Site. The Company can terminate or suspend access to the Site without notice for conduct violations or harmful behaviour. The Company can discontinue the Site and terminate the Agreement at any time without notice.</p>
          </Section>

          <Section n="8" title="Disclaimer of Warranty and Limitation of Liability">
            <p>The Company is not responsible for the content on the Site and makes no representations about its suitability, accuracy, or reliability. All content and functionality are provided "as is" without warranties, either express or implied. Users use the Site at their own risk; the Company does not warrant uninterrupted or error-free service. The Company is not liable for any indirect, incidental, consequential, or punitive damages, or lost revenues or profits. The Company is not liable for any damages resulting from use of the Site, including data loss, computer failure, or other issues.</p>
          </Section>

          <Section n="9" title="Indemnity">
            <p>Users agree to indemnify, defend, and hold the Company harmless from any loss, cost, damages, liability, or expense arising from third-party claims, fraud, or other breaches of the Agreement.</p>
          </Section>

          <Section n="10" title="No Agency or Partnership">
            <p>No agency, partnership, joint venture, or employment relationship is created as a result of this Agreement. The parties are independent contractors.</p>
          </Section>

          <Section n="11" title="Third-Party Websites">
            <p>The Company may provide links to third-party websites but is not responsible for their content or availability. Third-party websites are governed by their own terms of use and privacy policies.</p>
          </Section>

          <Section n="12" title="General">
            <p>By using the Site and its services, you agree to comply with the terms of this User Agreement and applicable laws. You must not copy, reproduce, sell, or trade any resources or material from the Site without permission. To register, you need to provide accurate personal information and are responsible for any false information. You must be authorized to use the payment methods provided on the Site. Unauthorized use may result in legal action. Activities that disrupt or interfere with our services, including hacking, are prohibited and may lead to legal action.</p>
          </Section>

          <Section n="13" title="Severability">
            <p>If the Company prevails in any legal action to enforce this Agreement, it will be entitled to costs and attorney fees. If any part of this Agreement is found unenforceable, it will be limited to the minimum extent necessary, and the rest of the Agreement will remain in effect. This Agreement represents the entire understanding between the parties and cancels any prior agreements. The Company may modify the terms by posting notice on the Site. Users are bound by these revisions and should review the terms periodically.</p>
          </Section>

          <Section n="14" title="Governing Law">
            <p>These Terms and Conditions, and any disputes or claims arising out of or in connection with them or their subject matter, shall be governed by and construed in accordance with the laws of New Zealand.</p>
            <p>Triptap complies with all applicable New Zealand laws and regulations, including but not limited to:</p>
            <Bullets items={[
              ["Fair Trading Act 1986", "Ensuring accurate representation of services and preventing misleading or deceptive conduct."],
              ["Privacy Act 2020", "Regulating the collection, storage, and use of personal information in compliance with privacy standards."],
              ["Land Transport Act 1998", "Ensuring all drivers and vehicles meet legal requirements for safety and licensing."],
              ["Small Passenger Service Operators (SPSO) Rules", "Maintaining compliance with safety and operational requirements specific to ride share services."],
              ["Income Tax Act 2007 and GST Regulations", "Complying with New Zealand tax laws for business operations and driver earnings."],
            ]} />
            <p>Any disputes arising from these Terms and Conditions or your use of the Triptap platform shall be resolved in accordance with New Zealand law and subject to the mediator or exclusive jurisdiction of the New Zealand courts.</p>
          </Section>

          <div className="pt-6 border-t border-white/10 text-xs text-slate-500">
            Last updated: {new Date().toLocaleDateString("en-NZ", { year: "numeric", month: "long", day: "numeric" })}
            <span className="mx-2">·</span>
            Questions? <Link to="/privacy" className="text-red-300 hover:text-red-200 underline underline-offset-2">Read our Privacy Policy</Link>
          </div>
        </article>
      </main>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section className="scroll-mt-24" id={`section-${n}`}>
    <div className="flex items-baseline gap-3 mb-3">
      <span className="text-red-400 font-mono text-sm">§{n}</span>
      <h2 className="font-display font-bold text-xl sm:text-2xl text-white">{title}</h2>
    </div>
    <div className="space-y-3 text-slate-300 leading-relaxed text-[15px]">{children}</div>
  </section>
);

const Sub = ({ label, children }) => (
  <div>
    <div className="text-white font-semibold mb-0.5">{label}</div>
    <div className="text-slate-300">{children}</div>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map(([label, body], i) => (
      <li key={i} className="flex gap-2">
        <span className="text-red-400 mt-2 flex-shrink-0 w-1 h-1 rounded-full bg-red-400"></span>
        <span><b className="text-white">{label}:</b> {body}</span>
      </li>
    ))}
  </ul>
);
